import type { IsoDate, IsoDateTime } from "./dates";
import { isBefore } from "./dates";

/** Roast level attribute; "unknown" contributes no offset and is never learned. */
export type RoastLevel = "light" | "medium_light" | "medium" | "medium_dark" | "dark" | "unknown";
/** Processing method attribute; seeded at zero everywhere and learned. */
export type ProcessClass = "washed" | "honey" | "natural" | "anaerobic" | "unknown";
/** Density class: high = dense high-grown (Geisha, SL28, high-altitude Bourbon); low = low-grown / Robusta blends. */
export type DensityClass = "high" | "standard" | "low" | "unknown";

/** Optional taste note on a shot; stored only, the predictor never reads it. */
export type Taste = "sour" | "balanced" | "bitter";
export const TASTES: readonly Taste[] = ["sour", "balanced", "bitter"];
export function tasteFrom(v: string | null | undefined): Taste | null {
  return (TASTES as readonly string[]).includes(v ?? "") ? (v as Taste) : null;
}

export const ROAST_LEVELS: readonly RoastLevel[] = ["light", "medium_light", "medium", "medium_dark", "dark", "unknown"];
export const PROCESS_CLASSES: readonly ProcessClass[] = ["washed", "honey", "natural", "anaerobic", "unknown"];
export const DENSITY_CLASSES: readonly DensityClass[] = ["high", "standard", "low", "unknown"];

export function roastLevelFrom(v: string | null | undefined): RoastLevel {
  return (ROAST_LEVELS as readonly string[]).includes(v ?? "") ? (v as RoastLevel) : "unknown";
}
export function processClassFrom(v: string | null | undefined): ProcessClass {
  return (PROCESS_CLASSES as readonly string[]).includes(v ?? "") ? (v as ProcessClass) : "unknown";
}
export function densityClassFrom(v: string | null | undefined): DensityClass {
  return (DENSITY_CLASSES as readonly string[]).includes(v ?? "") ? (v as DensityClass) : "unknown";
}

/** How a dial is written (display only). macro_micro: x = macro + letterIndex/9, A = 0 … I = 8, e.g. "9E". */
export type DialFormat = "decimal" | "macro_micro";

/**
 * Which way the numbers run. higher_finer grinders are mirrored by the app (x = min + max − dial) so the engine
 * always sees "higher = coarser" and a negative slope. Display only, like DialFormat.
 */
export type DialDirection = "higher_coarser" | "higher_finer";

/** A grinder with a stepless (or finely stepped) dial. Mirrors Grinder.java. */
export interface Grinder {
  readonly id: number | null;
  readonly name: string;
  readonly minGrind: number;
  readonly maxGrind: number;
  /** smallest meaningful step on the dial; used for rounding and bound margins */
  readonly dialResolution: number;
  /** baseline slope of ln(time) vs grind setting; must be negative */
  readonly priorSlopeB: number;
  /** owner-entered age-0 setting for a reference bean; null = use the midpoint */
  readonly referenceSetting: number | null;
  /** display only; the predictor ignores it */
  readonly dialFormat: DialFormat;
  /** display only; settings are stored mirrored when higher numbers are finer */
  readonly dialDirection: DialDirection;
  /** set when the grinder was recalibrated; earlier shots stay stored but the app leaves them out of predictions */
  readonly calibratedSince: IsoDateTime | null;
}

export function grinder(g: Grinder): Grinder {
  if (g.minGrind >= g.maxGrind) throw new RangeError("minGrind must be < maxGrind");
  if (g.dialResolution <= 0) throw new RangeError("dialResolution must be positive");
  if (g.priorSlopeB >= 0) throw new RangeError(`priorSlopeB must be negative, got ${g.priorSlopeB}`);
  if (g.referenceSetting !== null && (g.referenceSetting < g.minGrind || g.referenceSetting > g.maxGrind)) {
    throw new RangeError("referenceSetting must lie within the dial range");
  }
  return g;
}

export function midpoint(g: Grinder): number {
  return (g.minGrind + g.maxGrind) / 2.0;
}

export function clampToDial(g: Grinder, x: number): number {
  return Math.max(g.minGrind, Math.min(g.maxGrind, x));
}

/** Round to the nearest dial step, then tidy float noise to 3 dp. Java: Math.round(x/res)*res → round 3 dp. */
export function roundToDial(g: Grinder, x: number): number {
  const stepped = Math.round(x / g.dialResolution) * g.dialResolution;
  return Math.round(stepped * 1000.0) / 1000.0;
}

/** A bag of coffee. Mirrors Bean.java. */
export interface Bean {
  readonly id: number | null;
  readonly name: string;
  readonly roaster: string | null;
  readonly roastDate: IsoDate;
  /** date the bag went into the freezer; null = never frozen. Freezing stops the aging clock. */
  readonly freezeDate: IsoDate | null;
  readonly archived: boolean;
  readonly variety: string | null;
  readonly region: string | null;
  /** free text from import; the structured attribute is processClass */
  readonly process: string | null;
  readonly producer: string | null;
  readonly sourceUrl: string | null;
  readonly labelImagePath: string | null;
  readonly roastLevel: RoastLevel;
  readonly processClass: ProcessClass;
  readonly densityClass: DensityClass;
  readonly decaf: boolean;
  /** per-bean target shot time, or null for the recipe default */
  readonly targetTimeS: number | null;
}

export function bean(b: Bean): Bean {
  if (!b.roastDate) throw new RangeError("roastDate is required");
  if (b.freezeDate !== null && isBefore(b.freezeDate, b.roastDate)) {
    throw new RangeError("freezeDate cannot be before roastDate");
  }
  if (b.targetTimeS !== null && b.targetTimeS <= 0) throw new RangeError("targetTimeS must be positive");
  return b;
}

/** Convenience factory matching Bean.of(...): unknown attributes, caffeinated, recipe target. */
export function simpleBean(
  id: number | null,
  name: string,
  roaster: string | null,
  roastDate: IsoDate,
  freezeDate: IsoDate | null,
): Bean {
  return bean({
    id, name, roaster, roastDate, freezeDate, archived: false,
    variety: null, region: null, process: null, producer: null, sourceUrl: null, labelImagePath: null,
    roastLevel: "unknown", processClass: "unknown", densityClass: "unknown", decaf: false, targetTimeS: null,
  });
}

/** One logged espresso shot. Mirrors Shot.java. */
export interface Shot {
  readonly id: number | null;
  readonly beanId: number;
  readonly grinderId: number;
  /** when the shot was pulled, "yyyy-MM-ddTHH:mm:ss" */
  readonly timestamp: IsoDateTime;
  readonly doseG: number;
  readonly yieldG: number;
  /** seconds, to 0.1 s, from pump start including any pre-infusion */
  readonly timeSeconds: number;
  readonly grindSetting: number;
  /** 1–5 or null */
  readonly rating: number | null;
  readonly notes: string;
  /** recommendation shown when the shot was saved (backtesting only) */
  readonly predictedGrind: number | null;
  /** slope the predictor used at that moment (backtesting only) */
  readonly predictedSlope: number | null;
  /** CONFIG_VERSION in force (backtesting only) */
  readonly configVersion: string | null;
  /** sour / balanced / bitter, or null; stored only, not read by the predictor */
  readonly taste: Taste | null;
}

export function shot(s: Shot): Shot {
  if (!s.timestamp) throw new RangeError("timestamp is required");
  if (s.yieldG <= 0) throw new RangeError("yieldG must be positive");
  if (s.timeSeconds <= 0) throw new RangeError("timeSeconds must be positive");
  if (s.rating !== null && (s.rating < 1 || s.rating > 5 || !Number.isInteger(s.rating))) {
    throw new RangeError("rating must be 1–5 or null");
  }
  return s;
}

/** Build a shot without the prediction fields (they are stamped by the app on save). */
export function newShot(s: Omit<Shot, "predictedGrind" | "predictedSlope" | "configVersion" | "taste"> & { taste?: Taste | null }): Shot {
  return shot({ ...s, predictedGrind: null, predictedSlope: null, configVersion: null, taste: s.taste ?? null });
}

/** Output of predict(). Mirrors Prediction.java. */
export interface Prediction {
  /** recommended setting, rounded to the dial resolution */
  readonly grindSetting: number;
  readonly grindSettingRaw: number;
  /** ± in grind units; NaN when unknown */
  readonly uncertainty: number;
  readonly slopeUsed: number;
  /** ln(a) of the fitted line; NaN when no regression was fitted */
  readonly lnIntercept: number;
  /** 1 − w: how much of the slope came from the grinder prior */
  readonly priorWeight: number;
  readonly validShots: number;
  readonly censoredShotsUsed: number;
  readonly atBound: boolean;
  readonly conflictingBounds: boolean;
  /** the starting-point pseudo-observation's setting, or NaN when none */
  readonly xPrior: number;
  /** where the starting point came from (empty when none) */
  readonly priorSource: string;
  readonly explanation: string;
}

export function hasData(p: Prediction): boolean {
  return p.validShots > 0;
}

export function hasPrior(p: Prediction): boolean {
  return !Number.isNaN(p.xPrior);
}
